import { supabase } from '../../lib/supabase'
import { periodNumberFor } from '../../lib/payPeriods'
import { todayIso } from '../reports/reportDates'

export type OvertimeEntry = { name: string; hours: number; date: string }
// How many of the agents a published version names have acknowledged it, and
// who has not. Only agents with a login can, so only they are counted.
export type AcknowledgementProgress = { acknowledged: number; total: number; waiting: string[] }

export type PublishedItem = {
  title: string
  detail: string
  acknowledgement?: AcknowledgementProgress
  // What a reminder is asked of, and whether this is the version agents are
  // asked about now: only the latest version of a period can be reminded.
  // A period that has ended is no longer asked about, so is not reminded.
  version: { teamId: string; periodStart: string; version: number; latest: boolean; ended: boolean }
}

type OvertimeRow = { date: string; planned_overtime_hours: number | string; employees: { display_name: string } | null }

// Every planned overtime entry a team has in one pay period
// (schedule_entries.planned_overtime_hours), soonest first. Row-level
// security already limits schedule_entries to the caller's own teams; the
// team filter selects one of them.
export async function getTeamOvertime(teamId: string, periodStart: string, periodEnd: string): Promise<OvertimeEntry[]> {
  const { data, error } = await supabase
    .from('schedule_entries')
    .select('date, planned_overtime_hours, employees(display_name)')
    .eq('team_id', teamId)
    .gt('planned_overtime_hours', 0)
    .gte('date', periodStart)
    .lte('date', periodEnd)
    .order('date', { ascending: true })
  if (error || !data) return []
  return (data as unknown as OvertimeRow[]).flatMap((row) =>
    row.employees ? [{ name: row.employees.display_name, hours: Number(row.planned_overtime_hours), date: row.date }] : [],
  )
}

type VersionRow = {
  period_start: string
  period_end: string
  version: number
  published_at: string
  entries: { employeeId?: string; employeeName?: string }[]
}

const PAGE_SIZE = 1000

// Every row of a query, read a page at a time: the API returns at most one page
// of rows, and a count built from a partial read would be wrong. A failed page
// is a failed read, not a shorter one.
async function readAll<Row>(page: (first: number, last: number) => PromiseLike<{ data: unknown; error: unknown }>): Promise<Row[] | null> {
  const rows: Row[] = []
  for (let first = 0; ; first += PAGE_SIZE) {
    const { data, error } = await page(first, first + PAGE_SIZE - 1)
    if (error || !Array.isArray(data)) return null
    rows.push(...(data as Row[]))
    if (data.length < PAGE_SIZE) return rows
  }
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// A team's most recent publications across every pay period, newest
// first: version 1 is a period's initial publish and any later version an
// amendment (muster.schedule_versions), each with how many of the agents it
// names have acknowledged it (muster.schedule_acknowledgements).
export async function getRecentPublications(teamId: string, limit = 3): Promise<PublishedItem[]> {
  const { data, error } = await supabase
    .from('schedule_versions')
    .select('period_start, period_end, version, published_at, entries')
    .eq('team_id', teamId)
    .order('published_at', { ascending: false })
    .limit(limit)
  if (error || !data) return []
  const rows = data as unknown as VersionRow[]

  // Only the displayed periods are read, a page at a time, so a long history
  // does not run into the API's row limit and read as missing.
  const periods = Array.from(new Set(rows.map((row) => row.period_start)))
  const everyVersion = await readAll<{ period_start: string; version: number }>((first, last) =>
    supabase.from('schedule_versions').select('period_start, version').eq('team_id', teamId).in('period_start', periods).order('period_start').order('version').range(first, last),
  )
  const latestOf = new Map<string, number>()
  for (const row of everyVersion ?? []) {
    latestOf.set(row.period_start, Math.max(latestOf.get(row.period_start) ?? 0, row.version))
  }

  const named = Array.from(new Set(rows.flatMap((row) => row.entries.flatMap((entry) => (entry.employeeId ? [entry.employeeId] : [])))))
  const [people, acknowledgements] = await Promise.all([
    named.length === 0 ? Promise.resolve({ data: [] }) : supabase.from('employees').select('id, display_name, profile_id').in('id', named),
    readAll<{ period_start: string; version: number; employee_id: string }>((first, last) =>
      supabase
        .from('schedule_acknowledgements')
        .select('period_start, version, employee_id')
        .eq('team_id', teamId)
        .in('period_start', periods)
        .order('period_start')
        .order('version')
        .order('employee_id')
        .range(first, last),
    ),
  ])
  // Counts built from part of the history would read acknowledged agents as waiting.
  if (!everyVersion || !acknowledgements) return []

  // An employee with no login cannot acknowledge, so is never waited on.
  const withLogin = new Map(
    ((people.data ?? []) as { id: string; display_name: string; profile_id: string | null }[]).filter((row) => row.profile_id).map((row) => [row.id, row.display_name]),
  )
  const done = new Set((acknowledgements ?? []).map((row) => `${row.period_start}|${row.version}|${row.employee_id}`))

  return rows.map((row) => {
    const period = `Pay Period ${periodNumberFor(row.period_start)}`
    const agents = Array.from(new Set(row.entries.flatMap((entry) => (entry.employeeId && withLogin.has(entry.employeeId) ? [entry.employeeId] : []))))
    const waiting = agents.filter((id) => !done.has(`${row.period_start}|${row.version}|${id}`)).map((id) => withLogin.get(id)!).sort((a, b) => a.localeCompare(b))
    return {
      title: row.version === 1 ? period : `${period} amendment`,
      detail: `Published ${shortDate(row.published_at)}`,
      version: { teamId, periodStart: row.period_start, version: row.version, latest: latestOf.get(row.period_start) === row.version, ended: row.period_end < todayIso() },
      ...(agents.length > 0 ? { acknowledgement: { acknowledged: agents.length - waiting.length, total: agents.length, waiting } } : {}),
    }
  })
}

type QualificationRow = {
  employee_id: string
  effective_from: string
  effective_to: string | null
  qualifications: { name: string } | null
}

// The qualifications each employee holds on a date, by employee id
// (employee_qualifications, effective-dated), what a staffing target's own
// qualification narrows a coverage count to.
export async function getQualificationsOn(date: string): Promise<Map<string, Set<string>>> {
  const { data, error } = await supabase
    .from('employee_qualifications')
    .select('employee_id, effective_from, effective_to, qualifications(name)')
    .lte('effective_from', date)
    .or(`effective_to.is.null,effective_to.gte.${date}`)
  const held = new Map<string, Set<string>>()
  if (error || !data) return held
  for (const row of data as unknown as QualificationRow[]) {
    if (!row.qualifications) continue
    const names = held.get(row.employee_id) ?? new Set<string>()
    names.add(row.qualifications.name)
    held.set(row.employee_id, names)
  }
  return held
}
